import { alpha, createTheme, ThemeOptions } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { brandColors, colors, fontFamilies, fontWeights, radius, shadows, statusColors } from './designTokens';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    /** One per page/piece: site hero, fair backwall. */
    display: CSSProperties;
    /** Serif quote / anchor phrase — the human voice of the brand. */
    citacao: CSSProperties;
  }
  interface TypographyVariantsOptions {
    display?: CSSProperties;
    citacao?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true;
    citacao: true;
  }
}

/**
 * Builds the platform MUI theme from the People Rock manual tokens.
 * Colour comes from surfaces and ink; coral is used as the energetic accent.
 */
export function buildTheme(): ReturnType<typeof createTheme> {
  const accent = colors.acento;
  const accentHover = colors.acentoTexto;
  const accentSoft = alpha(accent, 0.1);

  const themeOptions: ThemeOptions = {
    palette: {
      mode: 'light',
      primary: {
        main: accent,
        light: brandColors.terracotaClara,
        dark: accentHover,
        contrastText: colors.sobreAcento,
      },
      secondary: {
        main: colors.bemEstarTexto,
        light: colors.bemEstar,
        dark: statusColors.success,
        contrastText: colors.sobreAcento,
      },
      error: {
        main: statusColors.error,
        light: statusColors.error,
        dark: statusColors.error,
        contrastText: brandColors.branco,
      },
      warning: {
        main: statusColors.warning,
        light: statusColors.warning,
        dark: statusColors.warning,
        contrastText: brandColors.branco,
      },
      info: {
        main: colors.digital,
        light: colors.digital,
        dark: colors.tinta,
        contrastText: colors.tinta,
      },
      success: {
        main: statusColors.success,
        light: colors.bemEstar,
        dark: statusColors.success,
        contrastText: brandColors.branco,
      },
      background: {
        default: colors.superficie,
        paper: colors.superficieElevada,
      },
      text: {
        primary: colors.tinta,
        secondary: colors.tintaSuave,
        disabled: alpha(colors.tintaSuave, 0.6),
      },
      divider: colors.linha,
    },

    typography: {
      fontFamily: fontFamilies.sans,
      display: {
        fontFamily: fontFamilies.sans,
        fontSize: '4rem',
        lineHeight: 1,
        fontWeight: fontWeights.title,
        letterSpacing: 0,
      },
      h1: { fontSize: '2.5rem', lineHeight: 46 / 40, fontWeight: fontWeights.title, letterSpacing: 0 },
      h2: { fontSize: '1.75rem', lineHeight: 34 / 28, fontWeight: fontWeights.subtitle },
      h3: { fontSize: '1.25rem', lineHeight: 1.4, fontWeight: fontWeights.subtitle },
      h4: { fontSize: '1.125rem', lineHeight: 1.45, fontWeight: fontWeights.subtitle },
      h5: { fontSize: '1rem', lineHeight: 1.5, fontWeight: fontWeights.subtitle },
      h6: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: fontWeights.title },
      subtitle1: { fontSize: '1.0625rem', lineHeight: 27 / 17, fontWeight: fontWeights.body },
      subtitle2: { fontSize: '0.875rem', lineHeight: 22 / 14, fontWeight: fontWeights.subtitle },
      body1: { fontSize: '1.0625rem', lineHeight: 27 / 17, fontWeight: fontWeights.body },
      body2: { fontSize: '0.875rem', lineHeight: 22 / 14, fontWeight: fontWeights.body },
      button: { fontWeight: fontWeights.subtitle, textTransform: 'none' },
      caption: { fontSize: '0.875rem', lineHeight: 22 / 14 },
      overline: {
        fontSize: '0.75rem',
        lineHeight: 16 / 12,
        fontWeight: fontWeights.subtitle,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      citacao: {
        fontFamily: fontFamilies.serif,
        fontSize: '1.75rem',
        lineHeight: 36 / 28,
        fontWeight: 400,
        fontStyle: 'italic',
      },
    },

    // Base radius = radius-md; cards use radius-lg, pills use radius-seixo.
    shape: {
      borderRadius: radius.md,
    },

    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },

    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: radius.seixo,
            fontWeight: 500,
            paddingInline: 22,
            minHeight: 44,
            transition: 'background-color 180ms ease, border-color 180ms ease, color 180ms ease',
            '&.Mui-focusVisible': {
              outline: `2px solid ${colors.foco}`,
              outlineOffset: 2,
            },
          },
          containedPrimary: {
            backgroundColor: accent,
            color: colors.sobreAcento,
            '&:hover': {
              backgroundColor: accentHover,
            },
          },
          outlinedPrimary: {
            borderColor: alpha(accent, 0.5),
            color: colors.acentoTexto,
            backgroundColor: 'transparent',
            '&:hover': {
              borderColor: accent,
              backgroundColor: accentSoft,
            },
          },
          textPrimary: {
            color: colors.acentoTexto,
            '&:hover': {
              backgroundColor: accentSoft,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: colors.acentoTexto,
            transition: 'color 180ms ease',
            '&:hover': {
              color: accent,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.superficieElevada,
            color: colors.tinta,
            borderBottom: `1px solid ${colors.linha}`,
            boxShadow: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            backgroundColor: colors.superficieElevada,
            transition: 'border-color 180ms ease, box-shadow 180ms ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.linha,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.tintaSuave,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.foco,
              borderWidth: 2,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            border: `1px solid ${colors.linha}`,
            backgroundColor: colors.superficieElevada,
            // The brand prefers background colour to shadow.
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: colors.superficieElevada,
          },
          outlined: {
            border: `1px solid ${colors.linha}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
            boxShadow: shadows.suave,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.superficieElevada,
            borderRight: `1px solid ${colors.linha}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            transition: 'background-color 180ms ease, color 180ms ease',
            '&:hover': {
              backgroundColor: accentSoft,
            },
            '&.Mui-selected': {
              backgroundColor: alpha(accent, 0.14),
              color: colors.acentoTexto,
              '&:hover': {
                backgroundColor: alpha(accent, 0.2),
              },
            },
            '&.Mui-focusVisible': {
              outline: `2px solid ${colors.foco}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.md,
            backgroundColor: colors.superficieElevada,
            border: `1px solid ${colors.linha}`,
            boxShadow: shadows.suave,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: accentSoft,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.seixo,
            fontWeight: 500,
          },
          outlined: {
            border: `1px solid ${colors.linha}`,
            backgroundColor: colors.superficieElevada,
            color: colors.tinta,
          },
          filled: {
            border: 'none',
          },
          filledPrimary: {
            backgroundColor: accent,
            color: colors.sobreAcento,
          },
          filledSecondary: {
            backgroundColor: alpha(colors.bemEstar, 0.35),
            color: colors.tinta,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: 'light',
          },
          body: {
            // Flat surface: no gradients or textures.
            backgroundColor: colors.superficie,
            minHeight: '100vh',
          },
          '#root': {
            minHeight: '100vh',
          },
          '::selection': {
            backgroundColor: alpha(accent, 0.25),
            color: colors.tinta,
          },
          ':focus-visible': {
            outline: `2px solid ${colors.foco}`,
            outlineOffset: 2,
          },
          '*::-webkit-scrollbar': {
            width: 10,
            height: 10,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: colors.superficie,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: colors.linha,
            borderRadius: radius.seixo,
            border: `2px solid ${colors.superficie}`,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
}

const theme = buildTheme();

export default theme;
